"use client";

import { RotateCcw } from "lucide-react";

import { getCurrencySymbol } from "@/features/currency/_logic/iso-4217";

import { useEstimator } from "../../_context/EstimatorContext";
import { useUndoableEdit } from "../../_hooks/useUndoableEdit";
import type { CellLayout } from "../../_hooks/useComparisonRows";
import type { RouteFare } from "../../_logic/types";
import { originKey } from "../../_logic/workspace";
import { CellSkeleton } from "./CellSkeleton";
import { DashCell } from "./DashCell";
import { FlightSummary } from "./FlightSummary";
import { InlineNumberEdit } from "./InlineNumberEdit";

interface RouteFareCellProps {
  route: RouteFare;
  destination: string;
  /** `wide`: flight details on the left of the row, price on the right; otherwise stacked under the price. */
  layout: CellLayout;
  /** Show the fare for everyone on the route rather than one traveler; the amount isn't editable then. */
  forGroup?: boolean;
}

/**
 * One home city's fare to one destination: the live fare with its airports, a missing fare in
 * amber, or the planner's own fare marked as such. Click the amount to type a fare; reset puts
 * the live fare back. Both go through the workspace reducer with an undo toast.
 * @param props - The route and its destination city.
 * @returns The cell content.
 * @throws Never.
 */
export const RouteFareCell = ({ route, destination, layout, forGroup = false }: RouteFareCellProps) => {
  // Hooks
  const { workspace, money } = useEstimator();
  const apply = useUndoableEdit();

  // Derived
  const symbol = getCurrencySymbol(workspace.currency);
  const airportPair = route.airports ? `${route.airports.origin}–${route.airports.destination}` : null;
  const shown = route.usd === null ? "" : String(Math.round(money.fromUsd(route.usd) ?? route.usd));
  const commit = (text: string) => {
    const amount = Number(text.replace(/[^\d.]/g, ""));
    const usd = text && Number.isFinite(amount) ? money.toUsd(amount) : null;
    if (usd === null) return false;
    apply(
      { type: "setFareOverride", originKey: route.key, destination, usd },
      `Set ${route.origin} to ${destination} at ${money.format(usd)}`
    );
  };
  const fare = (usd: number) => (forGroup ? money.format(usd * route.count, true) : money.format(usd));
  const editable = (display: React.ReactNode) =>
    forGroup ? (
      display
    ) : (
      <InlineNumberEdit
        display={display}
        initial={shown}
        field={{ prefix: symbol }}
        label={`Fare from ${route.origin} to ${destination}`}
        onCommit={commit}
      />
    );

  // Render
  switch (route.status) {
    case "local":
      return <span className="text-xs font-medium text-gray-600">Local · no flight</span>;
    case "needsDates":
      return <DashCell />;
    case "loading":
      return <CellSkeleton captions={layout === "wide" ? 1 : 2} />;
    case "override":
      return (
        <span className="inline-flex flex-col items-end gap-0.5">
          {editable(<span className="text-indigo-700">{fare(route.usd ?? 0)}</span>)}
          <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600">
            Your fare
            {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
            <button
              type="button"
              aria-label={`Use the live fare from ${route.origin} to ${destination}`}
              onClick={() =>
                apply(
                  { type: "setFareOverride", originKey: route.key, destination, usd: null },
                  `Back to the live fare from ${route.origin}`
                )
              }
              className="relative after:absolute after:-inset-3 after:content-[''] rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <RotateCcw className="size-3" />
            </button>
          </span>
        </span>
      );
    case "priced": {
      const price = editable(<span className="font-semibold text-gray-900">{fare(route.usd ?? 0)}</span>);
      const details = route.flight ? (
        <FlightSummary
          flight={route.flight}
          fallbackRoute={airportPair}
          destination={destination}
          tripCabin={workspace.origins.find((origin) => originKey(origin) === route.key)?.cabin ?? workspace.cabin}
          layout={layout === "wide" ? "inline" : "stacked"}
        />
      ) : (
        airportPair && <span className="text-xs font-medium text-gray-600">{airportPair}</span>
      );
      if (layout === "wide") {
        return (
          <span className="flex w-full min-w-0 items-start justify-between gap-4">
            <span className="min-w-0 flex-1">{details}</span>
            <span className="min-w-18 shrink-0 text-right tabular-nums">{price}</span>
          </span>
        );
      }
      return (
        <span className="flex w-full min-w-0 flex-col items-end gap-1">
          {price}
          {details}
        </span>
      );
    }
    case "missing":
    case "unchecked":
      return (
        <span className="inline-flex flex-col items-end gap-0.5">
          {/* Hovering shows it can be typed in; no repeated "click to add". */}
          {editable(<span className="text-amber-700">{route.status === "missing" ? "No fare" : "Not checked"}</span>)}
          {/* Why there's no fare, quietly; "Not checked" already says it. */}
          {route.status === "missing" && route.reason && (
            <span
              title={route.reason}
              className="line-clamp-2 max-w-48 whitespace-normal text-right text-xs font-medium text-gray-600"
            >
              {route.reason}
            </span>
          )}
        </span>
      );
  }
};
